// Aggregation behind the "רכש לפי סיווג פריטים" bars (ClassificationAnalysis). The
// sibling of supplierSlices.js, and deliberately shaped like it — but the rules
// differ in two ways that are the whole point of the card's redesign.
//
// 1. "לא מסווג" IS NOT A CLASSIFICATION, so it never becomes a bar.
//    It is a measure of how much the chart cannot tell you. Left in the
//    ranking it would routinely be the biggest bar (46.7% on a real sample),
//    making the headline answer "unknown" — and it would dilute every real
//    classification's share, so a classification holding 10% of total spend but 19% of
//    CLASSIFIED spend would read as the smaller number. It is reported beside
//    the bars as coverage instead.
//
// 2. topN is 8, not 5.
//    Suppliers genuinely are "a few big ones plus a long tail". A classification
//    taxonomy is designed to SPREAD spend across 14–19 buckets, so a top-5 cut
//    would leave a remainder large enough to outrank real classifications. Eight of
//    ~16 typically carries most of the spend without the tail swallowing it.
//
// Colours come from classificationColors.classificationColor(), NOT a rank-indexed palette:
// a classification's colour is its identity across /analysis, /catalog and /budget,
// so it must not change when its rank does.

import { classificationColor } from './classificationColors';

export const TOP_N = 8;
// Same grey the untagged bucket uses elsewhere. Safe to share: "לא מסווג" is
// no longer a bar, so the two can never appear in one list.
export const REMAINDER_COLOR = '#D9D9D9';
export const REMAINDER_NAME = 'שאר הסיווגים';

// The null-classification row the server emits ("לא מסווג"). Matched on classification_id,
// never on the name: a product carrying another tenant's classification_id also comes
// back named "לא מסווג" but with a non-null id, and treating that as the
// untagged bucket would silently merge two different facts.
export const isUntaggedRow = (c) => c.classification_id == null;

/**
 * Rank classifications by spend and shape them into bar rows.
 *
 * Only positive spend counts. A classification can net negative when credits in the
 * window exceed purchases; a negative bar has no honest length, so it is
 * excluded and counted in `negativeCount` for the card to mention.
 *
 * `total` is the CLASSIFIED total — the sum of the positive classification rows and
 * therefore the denominator the percentages use. It is deliberately not the
 * server's `total` (which includes לא מסווג and negatives), so the bars sum to
 * 100% and the strip that reports it must be labelled "סה״כ רכש מסווג".
 */
export function buildClassificationSlices(classifications, { topN = TOP_N } = {}) {
  const classified = (classifications || []).filter(c => !isUntaggedRow(c));
  const ranked = classified
    .filter(c => (c.spend || 0) > 0)
    .sort((a, b) => (b.spend || 0) - (a.spend || 0));

  const total = ranked.reduce((sum, c) => sum + (c.spend || 0), 0);
  const negativeCount = classified.length - ranked.length;

  const showRemainder = ranked.length > topN;
  const tops = showRemainder ? ranked.slice(0, topN) : ranked;
  const rest = showRemainder ? ranked.slice(topN) : [];

  // `value` is the figure the bars are drawn from, the shares computed on,
  // and the hover readout's exact (unrounded) figure — always net, since that
  // is the only basis this card offers.
  const slices = tops.map((c, i) => ({
    classification_id: c.classification_id,
    name: c.name,
    value: c.spend || 0,
    color: classificationColor(c, i),
    pct: total ? ((c.spend || 0) / total) * 100 : 0,
  }));

  if (showRemainder) {
    const restValue = rest.reduce((a, c) => a + (c.spend || 0), 0);
    slices.push({
      classification_id: null,
      name: REMAINDER_NAME,
      value: restValue,
      color: REMAINDER_COLOR,
      pct: total ? (restValue / total) * 100 : 0,
      isRemainder: true,
      count: rest.length,
    });
  }

  return {
    slices,
    total,
    negativeCount,
    hasData: ranked.length > 0,
  };
}

// One decimal, dropping a trailing ".0" — same rule as pctLabel in
// supplierSlices.js, so the two cards render percentages identically.
export function pctLabel(n) {
  const r = Math.round(n * 10) / 10;
  return `${Number.isInteger(r) ? r : r.toFixed(1)}%`;
}
